import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar, MapPin, Users, Trophy, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';

interface Tournament {
  id: string;
  name: string;
  description: string;
  sport: string;
  tournament_type: string;
  age_group: string;
  skill_level: string;
  start_date: string;
  end_date: string;
  registration_deadline: string;
  location: string;
  venue_name: string;
  entry_fee: number;
  prize_pool: number;
  max_teams: number;
  current_teams: number;
  status: string;
}

const Tournaments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'my-tournaments'>('all');

  useEffect(() => {
    loadTournaments();
  }, [filter, user]);

  const loadTournaments = async () => {
    try {
      let query = supabase
        .from('tournaments')
        .select('*')
        .order('start_date', { ascending: true });

      if (filter === 'upcoming') {
        query = query.eq('status', 'registration_open');
      } else if (filter === 'my-tournaments' && user) {
        query = query.eq('organizer_id', user.id);
      } else {
        query = query.in('status', ['upcoming', 'registration_open', 'registration_closed', 'in_progress']);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTournaments(data || []);
    } catch (error) {
      console.error('Error loading tournaments:', error);
      toast({
        title: "Error",
        description: "Failed to load tournaments",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      registration_open: 'bg-green-600/20 text-green-500 border-green-600/30',
      registration_closed: 'bg-yellow-600/20 text-yellow-500 border-yellow-600/30',
      in_progress: 'bg-blue-600/20 text-blue-500 border-blue-600/30',
      upcoming: 'bg-purple-600/20 text-purple-500 border-purple-600/30',
      completed: 'bg-gray-600/20 text-gray-400 border-gray-600/30',
    };
    return colors[status] || 'bg-gray-600/20 text-gray-400';
  };

  return (
    <div className="min-h-screen flex flex-col bg-black">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}

      <main className="flex-1 container mx-auto px-4 py-8 pb-24 md:pb-8 mt-24">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Tournaments</h1>
            <p className="text-gray-400">Find and register for tournaments</p>
          </div>
          {user && (
            <Button
              onClick={() => navigate('/tournaments/create')}
              className="bg-sports-green hover:bg-sports-green/80"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Tournament
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            All Tournaments
          </Button>
          <Button
            variant={filter === 'upcoming' ? 'default' : 'outline'}
            onClick={() => setFilter('upcoming')}
          >
            Open Registration
          </Button>
          {user && (
            <Button
              variant={filter === 'my-tournaments' ? 'default' : 'outline'}
              onClick={() => setFilter('my-tournaments')}
            >
              My Tournaments
            </Button>
          )}
        </div>

        {/* Tournaments Grid */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading tournaments...</div>
        ) : tournaments.length === 0 ? (
          <Card className="bg-gray-900 border-gray-700">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400 mb-4">No tournaments found</p>
              {user && filter === 'all' && (
                <Button
                  onClick={() => navigate('/tournaments/create')}
                  className="bg-sports-green hover:bg-sports-green/80"
                >
                  Create Your First Tournament
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tournaments.map((tournament) => (
              <Card key={tournament.id} className="bg-gray-900 border-gray-700 hover:border-sports-green transition-colors">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-white text-lg">{tournament.name}</CardTitle>
                    <Badge className={getStatusBadge(tournament.status)}>
                      {tournament.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline" className="text-sports-green border-sports-green">
                      <Trophy className="h-3 w-3 mr-1" />
                      {tournament.sport}
                    </Badge>
                    <Badge variant="outline">{tournament.tournament_type.replace('_', ' ')}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-400 line-clamp-2">{tournament.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      <Calendar className="h-4 w-4 text-sports-green" />
                      <span>
                        {format(new Date(tournament.start_date), 'MMM dd')} - {format(new Date(tournament.end_date), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-300">
                      <MapPin className="h-4 w-4 text-sports-green" />
                      <span className="truncate">{tournament.venue_name || tournament.location}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-300">
                      <Users className="h-4 w-4 text-sports-green" />
                      <span>
                        {tournament.current_teams}/{tournament.max_teams || '∞'} teams
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2 text-gray-300">
                        <DollarSign className="h-4 w-4 text-sports-green" />
                        <span className="font-semibold text-white">
                          ${tournament.entry_fee} entry
                        </span>
                      </div>
                      {tournament.prize_pool && (
                        <div className="text-sports-green text-sm">
                          ${tournament.prize_pool} prize
                        </div>
                      )}
                    </div>

                    {tournament.registration_deadline && (
                      <div className="text-xs text-yellow-500">
                        Register by {format(new Date(tournament.registration_deadline), 'MMM dd, yyyy')}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => navigate(`/tournaments/${tournament.id}`)}
                    className="w-full mt-4"
                    variant="outline"
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default Tournaments;
